<template>
    <div class="signature-pad-topaz">
        <div class="mb-3 privacy-consent border rounded p-3 bg-light">

            <h5 class="mb-2">Firma de consentimiento de datos</h5>
            <label class="d-flex align-items-start mb-0">
                <input
                    v-model="privacyAccepted"
                    type="checkbox"
                    class="mt-1 mr-2"
                    required
                />
                <span class="small">
                    Acepto el tratamiento de mis datos personales según se describe en la
                    <template v-if="policyUrl">
                        <a
                            :href="policyUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            >política de privacidad / tratamiento de datos</a
                        >.
                    </template>
                    <template v-else>
                        <span class="text-muted">
                            (El administrador del cliente debe configurar el enlace a la
                            política en «Tratamiento de datos».)
                        </span>
                    </template>
                </span>
            </label>

            <template v-if="!signatureFallback">
                <canvas
                    ref="canvas"
                    width="500"
                    height="200"
                    class="signature-pad-topaz__canvas border border-dark bg-white"
                    id="signature-pad-topaz-canvas"
                />

                <div class="mt-2">
                    <b-button size="sm" variant="primary" class="mr-1" @click="startCapture">
                        Iniciar firma
                    </b-button>
                    <b-button size="sm" variant="secondary" class="mr-1" @click="clearSignature">
                        Limpiar
                    </b-button>
                    <b-button size="sm" variant="success" @click="saveSignature">
                        Guardar firma
                    </b-button>
                </div>

                <p v-if="error" class="text-danger small mt-2 mb-0">{{ error }}</p>
            </template>

            <template v-else>
                <p class="text-muted small mb-2">
                    Tableta Topaz no disponible. Firme en el panel con el mouse o la
                    pantalla táctil.
                </p>
                <div class="signature-pad-topaz__vue-fallback">
                    <app-vue-signature
                        ref="vueSignaturePad"
                        width="100%"
                        height="200px"
                    />
                </div>
                <div class="mt-2">
                    <b-button size="sm" variant="secondary" class="mr-1" @click="undoVueSignature">
                        Borrar
                    </b-button>
                    <b-button size="sm" variant="success" @click="saveVueSignature">
                        Guardar firma
                    </b-button>
                </div>
                <p v-if="fallbackError" class="text-danger small mt-2 mb-0">{{ fallbackError }}</p>
            </template>
        </div>
    </div>

</template>

<script>
import VueSignature from 'vue-signature-pad'

export default {
    name: 'SignaturePadTopaz',

    components: {
        appVueSignature: VueSignature,
    },

    props: {
        value: { type: String, default: '' },
        policyUrl: { type: String, default: '' },
    },

    data() {
        return {
            error: null,
            fallbackError: null,
            privacyAccepted: false,
            sign: null,
            connected: false,
            signatureFallback: false,
        }
    },

    async mounted() {
        if (this.$refs.canvas) {
            this.ctx = this.$refs.canvas.getContext('2d')
        }
        try {
            await this.loadSigPlusExtLiteWrapperScript()
        } catch (e) {
            console.warn('[SignaturePadTopaz] SigPlusExtLite wrapper:', e)
        }
        await this.initTopaz()
    },

    methods: {
        /**
         * Loads the SigPlusExtLite script from the URL in
         * <html SigPlusExtLiteWrapperURL="https://..."> (same as document.write pattern, SPA-safe).
         */
        loadSigPlusExtLiteWrapperScript() {
            return new Promise((resolve, reject) => {
                const url = document.documentElement.getAttribute(
                    'SigPlusExtLiteWrapperURL'
                )
                if (!url || !String(url).trim()) {
                    resolve(null)
                    return
                }

                const src = String(url).trim()

                if (
                    document.querySelector(
                        'script[data-sigplus-ext-lite-wrapper="1"]'
                    )
                ) {
                    resolve(src)
                    return
                }

                const existing = Array.from(
                    document.querySelectorAll('script[src]')
                ).find(s => s.getAttribute('src') === src)
                if (existing) {
                    resolve(src)
                    return
                }

                const script = document.createElement('script')
                script.src = src
                script.async = true
                script.setAttribute('data-sigplus-ext-lite-wrapper', '1')
                script.onload = () => resolve(src)
                script.onerror = () =>
                    reject(
                        new Error(
                            'No se pudo cargar el script SigPlusExtLite desde ' +
                                src
                        )
                    )
                document.head.appendChild(script)
            })
        },

        async initTopaz() {
            try {
                if (typeof Topaz === 'undefined') {
                    this.error =
                        'Topaz SDK no está en la página. Defina en <html> el atributo SigPlusExtLiteWrapperURL apuntando al script del fabricante, o incluya el SDK en el bundle.'
                    return
                }

                const global = Topaz.Global

                const status = await global.Connect()
                console.log('device status', await global.GetDeviceStatus())

                if (status !== 1) {
                    let detail = ''
                    try {
                        detail = await global.GetLastError()
                    } catch (err) {
                        detail = ''
                    }
                    // Caso habitual en desarrollo / PC sin extensión ni tableta: no spamear console.error
                    this.error =
                        (detail && String(detail).trim()) ||
                        'No se pudo conectar con la tableta Topaz. Instale SigPlusExtLite y el controlador, y use un navegador compatible (p. ej. con la extensión del fabricante).'
                    return
                }

                const device = await global.GetDeviceStatus()
                console.log('device', device)

                if (device === 0) {
                    this.error =
                        'No hay tableta Topaz conectada o no detectada. Verifique USB y el controlador.'
                    return
                }

                this.sign = Topaz.Canvas.Sign
                this.connected = true
                this.error = null
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.debug('[SignaturePadTopaz] initTopaz', e)
                }
                this.error =
                    (e && e.message) ||
                    'Error al inicializar Topaz. Compruebe SigPlusExtLite y la tableta.'
            } finally {
                if (!this.connected) {
                    this.signatureFallback = true
                    this.error = null
                }
            }
        },

        async startCapture() {
            if (!this.connected || !this.sign) {
                this.error = 'Topaz no disponible'
                return
            }

            this.error = null

            try {
                this.clearSignature()
                await this.sign.SetTabletState(1)
                await this.sign.StartSign(this.$refs.canvas)
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.debug('[SignaturePadTopaz] startCapture', e)
                }
                this.error =
                    (e && e.message) || 'Error iniciando captura en la tableta'
            }
        },

        async clearSignature() {
            try {
                if (!this.sign) return

                await this.sign.SetTabletState(0)

                await this.sign.ClearSign()

                const canvas = document.getElementById('signature-pad-topaz-canvas')

                if (canvas) {

                    const ctx = canvas.getContext('2d')
                    ctx.clearRect(0, 0, canvas.width, canvas.height)
                }

                await this.sign.SetSigString('')

                await this.sign.SetTabletState(1)
                await this.sign.StartSign(canvas)

            } catch (e) {
                console.debug('[SignaturePadTopaz] clearSignature', e)
            }
        },

        async saveSignature() {
            if (!this.privacyAccepted) {
                this.error =
                    'Debe aceptar la política de tratamiento de datos'
                return
            }

            if (!this.sign) {
                this.error = 'Topaz no inicializado'
                return
            }

            try {
                const points = await this.sign.GetTotalPoints()

                if (points <= 0) {
                    this.error = 'No hay firma'
                    return
                }

                const firma = await this.sign.GetSignatureImage()

                this.error = null
                this.$emit('input', firma)

            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.debug('[SignaturePadTopaz] saveSignature', e)
                }
                this.error =
                    (e && e.message) || 'Error al obtener la firma'
            }
        },

        undoVueSignature() {
            this.fallbackError = null
            const pad = this.$refs.vueSignaturePad
            if (pad && typeof pad.undoSignature === 'function') {
                pad.undoSignature()
            }
        },

        saveVueSignature() {
            this.fallbackError = null
            if (!this.privacyAccepted) {
                this.fallbackError =
                    'Debe aceptar la política de tratamiento de datos'
                return
            }
            const pad = this.$refs.vueSignaturePad
            if (!pad || typeof pad.saveSignature !== 'function') {
                this.fallbackError = 'Panel de firma no disponible'
                return
            }
            const { data, isEmpty } = pad.saveSignature()
            if (isEmpty || !data) {
                this.fallbackError = 'No hay firma'
                return
            }
            this.$emit('input', data)
        },

        async reset() {
            this.privacyAccepted = false
            this.error = null
            this.fallbackError = null
            if (this.signatureFallback) {
                this.$nextTick(() => {
                    const pad = this.$refs.vueSignaturePad
                    if (pad && typeof pad.clearSignature === 'function') {
                        pad.clearSignature()
                    }
                })
                return
            }
            await this.clearSignature()
        },
    },
}
</script>

<style scoped>
.signature-pad-topaz__canvas {
    max-width: 100%;
    height: auto;
}

/* Match FormatRegister.vue signature pad frame (vue-signature-pad inner canvas) */
.signature-pad-topaz__vue-fallback ::v-deep canvas {
    border: double 3px transparent;
    border-radius: 5px;
    background-image: linear-gradient(white, white),
        radial-gradient(circle at top left, #4bc5e8, #9f6274);
    background-origin: border-box;
    background-clip: content-box, border-box;
}
</style>
